import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import { Box, Stack } from '@mui/material';
import { useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API } from 'utilities/api';
import { backendURL } from 'config';
import { headerJson } from 'utilities/common';
import {
  EmptyState, LoadingOverlay, PageHeader, PagePaper,
} from 'dashboard/components/common';
import OrderDetailDialog from './OrderDetailDialog';
import BulkActionToolbar from './BulkActionToolbar';
import OrderPager from './OrderPager';
import OrderKpiRow from './OrderKpiRow';
import OrderFilterBar from './OrderFilterBar';
import OrderTable from './OrderTable';

const BULK_LABELS = { 'mark-paid': 'Mark paid', fulfill: 'Mark fulfilled', cancel: 'Cancel' };

/**
 * Live brand order pipeline. Owner-side list + status transitions.
 *
 * This file owns: data fetching, search/filter/page state, selection
 * state, bulk-action handling, and the CSV export. The visual pieces
 * (KPI row, filter bar, table, pager, toolbar, detail dialog) each
 * live in their own files.
 */
export default function Orders() {
  const [selectedBrand] = useOutletContext();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [dlgOpen, setDlgOpen] = useState(false);
  const focusedIdRef = useRef(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      focusedIdRef.current = params.get('focus');
    } catch (_) { /* ignore */ }
  }, []);

  const reload = useCallback(() => {
    if (!selectedBrand) return Promise.resolve();
    setLoading(true);
    return API.listBrandOrdersPage({
      brand_id: selectedBrand,
      status: statusFilter || undefined,
      search: search || undefined,
      page,
      page_size: pageSize,
    })
      .then((res) => {
        setRows(Array.isArray(res?.items) ? res.items : []);
        setTotal(Number(res?.total || 0));
      })
      .catch((err) => toast.error(err?.message || 'Failed to load orders', { toastId: 'orders-load-error' }))
      .finally(() => setLoading(false));
  }, [selectedBrand, statusFilter, search, page, pageSize]);

  useEffect(() => { setPage(1); }, [statusFilter, search, selectedBrand]);
  useEffect(() => { setSelected(new Set()); }, [rows]);

  // Cancellable load so a brand switch / filter change mid-flight
  // doesn't write a stale response to state.
  useEffect(() => {
    if (!selectedBrand) return undefined;
    let cancelled = false;
    setLoading(true);
    API.listBrandOrdersPage({
      brand_id: selectedBrand,
      status: statusFilter || undefined,
      search: search || undefined,
      page,
      page_size: pageSize,
    })
      .then((res) => {
        if (cancelled) return;
        setRows(Array.isArray(res?.items) ? res.items : []);
        setTotal(Number(res?.total || 0));
      })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load orders', { toastId: 'orders-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedBrand, statusFilter, search, page, pageSize]);

  // Auto-open the detail dialog for a deep-linked order id (?focus=...).
  useEffect(() => {
    const target = focusedIdRef.current;
    if (!target || rows.length === 0) return;
    const found = rows.find((r) => String(r._id) === String(target));
    if (found) {
      setActive(found);
      setDlgOpen(true);
      focusedIdRef.current = null;
    }
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allSelected  = rows.length > 0 && rows.every((r) => selected.has(String(r._id)));
  const someSelected = !allSelected && rows.some((r) => selected.has(String(r._id)));

  const toggleAll = () => {
    setSelected((prev) => {
      if (allSelected) return new Set();
      const next = new Set(prev);
      rows.forEach((r) => next.add(String(r._id)));
      return next;
    });
  };

  const selectedRows = rows.filter((r) => selected.has(String(r._id)));

  // Per-action gate — `mark-paid` only makes sense for pending,
  // `fulfill` for paid, etc. Empty selection disables every action.
  const canBulk = (action) => {
    if (selectedRows.length === 0) return false;
    if (action === 'mark-paid') return selectedRows.every((r) => r.status === 'pending');
    if (action === 'fulfill')   return selectedRows.every((r) => r.status === 'paid');
    if (action === 'cancel')    return selectedRows.every((r) => ['pending', 'paid'].includes(r.status));
    return false;
  };

  const runBulk = async (action) => {
    const ids = selectedRows.map((r) => r._id);
    if (ids.length === 0) return;
    if (!window.confirm(`${BULK_LABELS[action] || action} ${ids.length} order${ids.length === 1 ? '' : 's'}?`)) return;
    setBulkBusy(true);
    try {
      const res = await API.bulkBrandOrders({ brand_id: selectedBrand, action, ids, args: {} });
      const okN = res?.succeeded || 0;
      toast.success(`${BULK_LABELS[action] || action}: ${okN} ok${res?.errors?.length ? `, ${res.errors.length} failed` : ''}`);
      if (res?.errors?.length) {
        res.errors.slice(0, 3).forEach((e) => toast.warn(`${e._id}: ${e.reason}`));
      }
      setSelected(new Set());
      reload();
    } catch (err) {
      toast.error(err?.message || 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const exportCsv = async () => {
    if (!selectedBrand) return;
    try {
      const res = await axios.post(
        `${backendURL}/dashboard/brand/orders/export-csv`,
        { brand_id: selectedBrand, status: statusFilter || undefined, limit: 1000 },
        { headers: headerJson(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders-${String(selectedBrand).slice(-6)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Export failed');
    }
  };

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <PagePaper>
        <PageHeader
          title='Orders'
          subtitle="Live order pipeline. Marking an order paid reserves the line items, decrements stock from the chosen warehouses, issues an invoice, and rolls into the customer's lifetime value."
        />

        {!selectedBrand && <EmptyState>Select a brand to view its orders.</EmptyState>}

        {selectedBrand && (
          <Stack spacing='14px'>
            <OrderKpiRow rows={rows} />

            <OrderFilterBar
              statusFilter={statusFilter}
              onStatusChange={setStatusFilter}
              search={search}
              onSearchChange={setSearch}
              onRefresh={reload}
              onExport={exportCsv}
              rowsShown={rows.length}
              exportDisabled={rows.length === 0}
            />

            <BulkActionToolbar
              selectedCount={selected.size}
              canBulk={canBulk}
              busy={bulkBusy}
              onRun={runBulk}
              onClear={() => setSelected(new Set())}
            />

            <LoadingOverlay loading={loading && rows.length === 0} minHeight={200}>
              <OrderTable
                rows={rows}
                loading={loading}
                selected={selected}
                onToggleRow={toggleRow}
                allSelected={allSelected}
                someSelected={someSelected}
                onToggleAll={toggleAll}
                onRowClick={(o) => { setActive(o); setDlgOpen(true); }}
              />
            </LoadingOverlay>

            <OrderPager
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              loading={loading}
              onPage={setPage}
            />
          </Stack>
        )}
      </PagePaper>

      {active && (
        <OrderDetailDialog
          open={dlgOpen}
          onClose={() => { setDlgOpen(false); setActive(null); }}
          brandId={selectedBrand}
          order={active}
          onChanged={reload}
        />
      )}
    </Box>
  );
}
